<script setup>
import { computed } from 'vue'
import { useI18n } from 'vue-i18n'
import LanguageSwitcher from './LanguageSwitcher.vue'
import { sanitizeHtml } from '../utils/sanitize'

const { t } = useI18n()

const emit = defineEmits(['close', 'continue'])

const sanitizedDescription = computed(() =>
  sanitizeHtml(t('adblock.description2', {
    bold_start: '<strong>',
    bold_end: '</strong>'
  }))
)

const handleContinue = () => {
    emit('continue');
    emit('close');
};

const refreshPage = () => {
    window.location.reload();
};
</script>

<template>
    <div class="fixed inset-0 z-[100] flex items-center justify-center p-4 px-4" role="dialog" aria-modal="true">
        <!-- Overlay -->
        <div class="absolute inset-0 bg-gray-900/80 backdrop-blur-sm transition-opacity"></div>

        <!-- Modal Content -->
        <div
            class="relative bg-white dark:bg-gray-800 rounded-2xl shadow-2xl max-w-lg w-full overflow-hidden transform transition-all border border-gray-100 dark:border-gray-700">

            <!-- Header Image / Brand Area -->
            <div
                class="bg-brand-background p-6 flex justify-center border-b border-gray-100 dark:border-gray-700 relative">
                <div class="absolute top-4 right-4">
                    <LanguageSwitcher />
                </div>
                <!-- Using an SVG icon representing the brand or ads -->
                <svg xmlns="http://www.w3.org/2000/svg" class="h-16 w-16 text-brand-accent" fill="none"
                    viewBox="0 0 24 24" stroke="currentColor">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="1.5"
                        d="M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z" />
                </svg>
            </div>

            <div class="p-8 text-center">
                <h3 class="text-2xl font-bold text-gray-900 dark:text-white mb-4">
                    {{ $t('adblock.title') }}
                </h3>

                <div class="text-gray-600 dark:text-gray-300 text-base leading-relaxed mb-8">
                    <p class="mb-4">
                        {{ $t('adblock.description1') }}
                    </p>
                    <p>
                        <span v-html="sanitizedDescription"></span>
                    </p>
                </div>

                <!-- Primary Action -->
                <div class="flex flex-col gap-3 items-center mb-6">
                    <button @click="refreshPage"
                        class="w-full sm:w-auto px-8 py-3 bg-brand-accent hover:bg-brand-accent/90 text-white font-bold rounded-full transition-colors shadow-lg shadow-brand-accent/30 text-lg uppercase tracking-wide">
                        {{ $t('adblock.refresh_button') }}
                    </button>

                    <a href="https://ko-fi.com/miguelmesachica" target="_blank" rel="noopener noreferrer"
                        class="w-full sm:w-auto px-8 py-3 bg-[#FF5E5B] hover:bg-[#FF5E5B]/90 text-white font-bold rounded-full transition-colors shadow-lg shadow-[#FF5E5B]/30 text-lg uppercase tracking-wide flex items-center justify-center gap-2">
                        <svg xmlns="http://www.w3.org/2000/svg" class="h-5 w-5" viewBox="0 0 24 24" fill="currentColor">
                            <path
                                d="M23.881 8.948c-.773-4.085-4.859-4.593-4.859-4.593H.723c-.604 0-.679.798-.679.798s-.082 7.324-.022 11.822c.164 2.424 2.586 2.672 2.586 2.672s8.267-.023 11.966-.049c2.438-.426 2.683-2.566 2.658-3.734 4.352.24 3.722-2.271 3.722-2.271l2.008-4.645h.919zm-5.493 3.924c-.606 1.006-2.457 1.107-2.457 1.107-6.066.307-13.685-.052-13.685-.052-1.996-.067-1.396-1.571-1.396-1.571s-.112-5.464-.099-6.953c.023-2.618 3.518-2.316 3.518-2.316l10.976.028c2.937.135 2.64 4.095 2.64 4.095-.015 3.123.504 5.662.504 5.662zM21.2 11.168l-1.353 3.167s-2.028.914-2.571.49l.646-1.78s-.686.035-1.282-.12c.002 0 .193 1.963 2.16 2.38 2.302.488 2.054-2.264 2.054-2.264l1.493-3.419h-1.147z" />
                        </svg>
                        {{ $t('adblock.support_kofi') }}
                    </a>
                </div>
            </div>

            <!-- CSS Grid for Footer Actions similar to the example -->
            <div
                class="bg-gray-100 dark:bg-gray-900 py-4 px-6 text-center text-sm border-t border-gray-200 dark:border-gray-700">
                <button @click="handleContinue"
                    class="text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200 font-medium underline-offset-2 hover:underline transition-colors mx-2">
                    {{ $t('adblock.continue_link') }}
                </button>
                <span class="text-gray-300 dark:text-gray-600">|</span>
                <a href="#"
                    class="text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200 font-medium underline-offset-2 hover:underline transition-colors mx-2">
                    {{ $t('footer.contact') }}
                </a>
            </div>
        </div>
    </div>
</template>
